import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Outlet, useLocation, useOutletContext } from 'react-router-dom';
import { FormikHelpers, useFormik } from 'formik';
import { RowSelectionType, RowSelectMethod } from 'antd/es/table/interface';

import { useGetUsersListByNameQuery } from '@Store/api/userApiSlice';
import {
  ApprovalHistory,
  RequestListSearchResponse,
} from '@Types/api/requestList';
import { NotificationContext } from '@Context/NotificationContext';
import { ApprovalHistory as ApprovalHistoryModal } from '@Components/ApprovalHistory';
import {
  PresentationContainer,
  PresentationRequestHeader,
} from '@Components/index';
import { ApprovalsRequestList } from '@Components/RequestList';
import { PAGE_SIZE, TABLE_PAGE_DEFAULT } from '@Features/constants';
import {
  useAppDispatch,
  useFindRequestType,
  useIsLoadedConent,
  useRequestRefetcher,
} from '@Hooks/index';
import { SortConfig } from '@Features/types/SortConfig';
import { FiltersWidget } from '@Components/FiltersWidget';
import { prepareSearchCriterias } from '@Features/utils/prepareSearchCriterias';
import { SearchCriteria } from '@Types/api/common/filters';

import { useRequisitionBatchApproveMutation } from '@Store/api/approvalsListApiSlice';

import { RequisitionApproveRejectRequest } from '@Types/api/form/materialRequisition/Approve/RequisitionApproveRequest';
import { ApprovalsTasksRequisitionResponse } from '@Types/api/approvalsList';
import { AnyObject } from 'antd/es/_util/type';
import { eventMessageActions } from '@Store/slices/eventMessageSlice';
import { MSkeleton } from '@Components/Base';
import { parseSelectedFiltersToOptions } from '@Features/utils/parseSelectedFiltersToOptions';
import { filterOptionsActions } from '@Store/slices/filterOptionsSlice';
import { mapColumns } from './list/MapColumns';
import { dataSourceMap } from './list/DataSource';
import ColumnHeader from './components/ColumnHeader';
import Filters from './search/Filter';
import { FilterForm } from './search/types';
import {
  filterTypes,
  getFiltersMapper,
  initialValuesFilterForm,
} from './search/filters.utils';
import styles from './task-list.module.css';
import { RequisitionStatuses } from '../enum/RequisitionStatuses.enum';

import { ColumnDataType } from './list/type';
import { useGetApprovalHistoryMutation } from '../requests/MaterialRequisition/store/api/MaterialRequestApiSlice';

type FiltersHandler = (
  values: FilterForm,
  formikHelpers: FormikHelpers<FilterForm>
) => void;

const TaskList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isRequestFormPage } = useOutletContext<{
    isRequestFormPage: boolean;
  }>();

  const isRequestFormPagePrev = useRef(isRequestFormPage);
  const [tagsValues, setTagsValues] = useState(initialValuesFilterForm);
  const [showTags, setShowTags] = useState(false);
  const [filters, setFilters] = useState<SearchCriteria[] | undefined>(
    undefined
  );
  const [sorting, setSorting] = useState<SortConfig | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<
    React.Key[] | undefined
  >(undefined);
  const [selectedTasksRow, setSelectedTasksRow] = useState<
    ApprovalsTasksRequisitionResponse[]
  >([]);
  const [searchValue, setSearchValue] = useState<string | undefined>(undefined);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const { errorNotification } = useContext(NotificationContext);
  const [getApprovalHistory] = useGetApprovalHistoryMutation();
  const [isApprovalHistoryOpen, setIsApprovalHistoryOpen] = useState(false);
  const [approvalHistoryData, setApprovalHistoryData] = useState<
    ApprovalHistory[] | null
  >(null);
  const { data: users = [] } = useGetUsersListByNameQuery(null);
  const { findRequestTypeBy } = useFindRequestType();

  const closeFilters = (): void => {
    setIsFiltersOpen(false);
  };

  const openFilters = (): void => {
    setIsFiltersOpen(true);
  };

  const handleApplyFilters: FiltersHandler = (options): void => {
    const filtersMapper = getFiltersMapper();
    const searchCriterias = prepareSearchCriterias(
      options,
      filterTypes,
      filtersMapper
    );

    setFilters(searchCriterias);

    const parsedOptions = parseSelectedFiltersToOptions(options);

    dispatch(filterOptionsActions.setFilterSettings(searchCriterias));
    dispatch(filterOptionsActions.setFilterOptions(parsedOptions));
    dispatch(eventMessageActions.sendEventType('FILTER'));

    setShowTags(true);
    setTagsValues(options);

    closeFilters();
  };

  const formik = useFormik({
    initialValues: initialValuesFilterForm,
    enableReinitialize: true,
    onSubmit: handleApplyFilters,
  });

  const clearParams = (): void => {
    setSorting(null);
    setFilters(undefined);
    setSearchValue(undefined);
    setTagsValues(initialValuesFilterForm);
    formik.setValues(initialValuesFilterForm);
  };

  const {
    currentPage,
    fetchRequest,
    handleLoadMore,
    onChangeCurrentPage,
    isSuccess,
    listRequests,
    requestsDataList,
  } = useRequestRefetcher({
    clearParams,
    filters,
    isRequestFormPage,
    isRequestFormPagePrev,
    requestType: 'MRTaskRequest',
    searchValue,
    sorting,
  });

  const isContentLoaded = useIsLoadedConent(isSuccess);

  const [batchApprove] = useRequisitionBatchApproveMutation();

  const { pathname } = useLocation();

  const dataSource = useMemo(() => {
    if (listRequests?.content) {
      return dataSourceMap(
        listRequests?.content as ApprovalsTasksRequisitionResponse[],
        findRequestTypeBy
      );
    }

    return [];
  }, [listRequests?.content]);

  const isOutlet = pathname.includes('approve-form');

  const handleChangePage = useCallback((selectedPage: number): void => {
    onChangeCurrentPage(selectedPage);
    dispatch(eventMessageActions.sendEventType('REFETCH'));
  }, []);

  const handleSort = useCallback((sortConfig: SortConfig | null) => {
    setSorting(sortConfig);
    onChangeCurrentPage(TABLE_PAGE_DEFAULT);
  }, []);

  useEffect(() => {
    isRequestFormPagePrev.current = isRequestFormPage;
  }, [isRequestFormPage]);

  const isSelected = Boolean(selectedRowKeys?.length);

  const selectedAllRows = useMemo(() => {
    if (dataSource?.length) {
      return selectedRowKeys?.length === dataSource?.length;
    }

    return false;
  }, [selectedRowKeys?.length, dataSource?.length]);

  const renderColumnTitle = useCallback(
    (originalNode: React.ReactNode) => (
      <div className={`${selectedAllRows ? 'trojan-selected-all' : ''}`}>
        {originalNode}
      </div>
    ),
    [selectedAllRows]
  );

  const isPurchaseOfficerRequest = ({
    statusId,
  }: ApprovalsTasksRequisitionResponse): boolean => {
    if (
      statusId === RequisitionStatuses.AWAITING_APPROVAL_OF_PROCUREMENT_MANAGER
    ) {
      return true;
    }
    return false;
  };

  const isPlantEngineerRequest = ({
    statusId,
  }: ApprovalsTasksRequisitionResponse): boolean => {
    if (
      statusId === RequisitionStatuses.AWAITING_APPROVAL_OF_SENIOR_STORE_KEEPER
    ) {
      return true;
    }

    return false;
  };

  const isPlantWorkshopManagerRequest = ({
    statusId,
  }: ApprovalsTasksRequisitionResponse): boolean => {
    if (statusId === RequisitionStatuses.AWAITING_APPROVAL_OF_PLANT_ENGINEER) {
      return true;
    }

    return false;
  };

  const isITAdminRequest = ({
    statusId,
  }: ApprovalsTasksRequisitionResponse): boolean => {
    if (
      statusId === RequisitionStatuses.AWAITING_APPROVAL_OF_WAREHOUSE_MANAGER
    ) {
      return true;
    }

    return false;
  };

  const rowSelection = useMemo(() => {
    return {
      onChange: (
        selectedKeys: React.Key[],
        selectedRows: Array<AnyObject>,
        info: {
          type: RowSelectMethod;
        }
      ) => {
        const tasksData =
          info.type === 'none'
            ? (selectedRows as ApprovalsTasksRequisitionResponse[])
            : (selectedRows as ColumnDataType[]).map((row) => row.taskData);

        const mappedSelectedTasks = tasksData
          ?.filter((item) => {
            if (
              isPurchaseOfficerRequest(item) ||
              isPlantEngineerRequest(item) ||
              isPlantWorkshopManagerRequest(item) ||
              isITAdminRequest(item)
            ) {
              return false;
            }
            return true;
          })
          .map((item) => item);

        const filtredSelectedKeys = mappedSelectedTasks.map(
          (task) => task.taskId as React.Key
        );

        setSelectedTasksRow(mappedSelectedTasks);
        setSelectedRowKeys(filtredSelectedKeys);
      },
      fixed: true,
      columnTitle: renderColumnTitle,
      selectedRowKeys,
      type: 'checkbox' as RowSelectionType,
    };
  }, [renderColumnTitle, selectedRowKeys]);

  const handleBulkApprove = useCallback(() => {
    const bulkItems = selectedTasksRow.map((task) => task.taskId);
    batchApprove({
      commands: bulkItems?.map((item) => ({
        taskId: item,
        comment: '',
        decision: 'Approve',
        selectedApproverUserId: 0,
      })) as RequisitionApproveRejectRequest[],
      command: 'Approve',
    })
      .unwrap()
      .then(() => {
        setSelectedTasksRow([]);
        setSelectedRowKeys(undefined);
        fetchRequest();
      })
      .catch(() => {});
  }, [selectedRowKeys, fetchRequest]);

  const handleApprovalHistory = useCallback((requestNo: string) => {
    getApprovalHistory(requestNo)
      .unwrap()
      .then((approvalHistoryResp) => {
        if (Array.isArray(approvalHistoryResp) && approvalHistoryResp.length) {
          setIsApprovalHistoryOpen(true);
          setApprovalHistoryData(approvalHistoryResp);
        }
      })
      .catch(({ data: error }) => {
        errorNotification(error);
      });
  }, []);

  const handleBulkReject = useCallback(() => {
    const bulkItems = selectedTasksRow.map((task) => task.taskId);
    batchApprove({
      commands: bulkItems?.map((item) => ({
        taskId: item,
        comment: '',
        decision: 'Reject',
        selectedApproverUserId: 0,
      })) as RequisitionApproveRejectRequest[],
      command: 'Reject',
    })
      .unwrap()
      .then(() => {
        setSelectedTasksRow([]);
        setSelectedRowKeys(undefined);
        fetchRequest();
      })
      .catch(() => {});
  }, [selectedRowKeys, fetchRequest]);

  const renderColumnHeader = useCallback(
    (): JSX.Element => (
      <ColumnHeader
        isAllSelected={selectedAllRows}
        count={selectedRowKeys?.length || 0}
        onApprove={handleBulkApprove}
        onReject={handleBulkReject}
      />
    ),
    [handleBulkApprove, handleBulkReject, selectedAllRows, selectedRowKeys]
  );

  const handleSearch = useCallback((value: string | null) => {
    setSearchValue(value || undefined);
  }, []);

  const tagsFormik = {
    ...formik,
    values: tagsValues,
  };

  return (
    <div className={styles.wrapper}>
      {isOutlet ? (
        <Outlet />
      ) : (
        <>
          <PresentationRequestHeader caption="Approvals" showSelect={false} />
          <PresentationContainer className={styles.taskList}>
            <FiltersWidget
              onSearchSubmit={handleSearch}
              isOpen={isFiltersOpen}
              onClose={closeFilters}
              onOpen={openFilters}
              formik={tagsFormik}
              showTags={showTags}
            >
              <Filters formik={formik} />
            </FiltersWidget>
            {isContentLoaded ? (
              <ApprovalsRequestList
                onLoadMore={handleLoadMore}
                listRequests={
                  listRequests as RequestListSearchResponse<ApprovalsTasksRequisitionResponse>
                }
                requestsDataList={
                  requestsDataList as Array<ApprovalsTasksRequisitionResponse>
                }
                selectedTasksRow={selectedTasksRow}
                requestType="MRTaskRequest"
                dataSource={dataSource}
                mapColumns={mapColumns}
                totalItems={listRequests?.totalElements}
                pageSize={PAGE_SIZE}
                onApproveReject={fetchRequest}
                onApprovalHistory={handleApprovalHistory}
                currentPage={currentPage}
                onChangePage={handleChangePage}
                onSort={handleSort}
                onBulkApprove={handleBulkApprove}
                onBulkReject={handleBulkReject}
                rowSelection={rowSelection}
                title={isSelected ? renderColumnHeader : undefined}
                isSearchApplied={Boolean(searchValue)}
              />
            ) : (
              <MSkeleton />
            )}
          </PresentationContainer>

          <ApprovalHistoryModal
            isOpen={isApprovalHistoryOpen}
            onClose={() => {
              setIsApprovalHistoryOpen(false);
            }}
            data={approvalHistoryData}
            users={users}
          />
        </>
      )}
    </div>
  );
};

export default TaskList;
